import { Component, signal, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, Validators } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { ImageTagComponent, ButtonComponent, InputComponent, PasswordComponent } from '../../ui';
import { ErrorService } from '../../../shared/services/error.service';
import { API_CONFIG } from '../../../config/api.config';
import { AuthUrls, ApiUrlBuilder } from '../../enums/api-urls/api-urls.enum';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';

@Component({
  selector: 'app-admin-reset-password',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ImageTagComponent,
    ButtonComponent,
    PasswordComponent
  ],
  templateUrl: './admin-reset-password.component.html',
  styleUrl: './admin-reset-password.component.css'
})
export class AdminResetPasswordComponent implements OnInit {
  private fb = inject(FormBuilder);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private http = inject(HttpClient);
  private errorService = inject(ErrorService);
  private baseUrl = API_CONFIG.getBaseUrl();

  isLoading = signal(false);
  passwordReset = signal(false);
  token = signal<string | null>(null);
  
  // Custom validators for password requirements
  hasUppercaseValidator = (control: any) => {
    if (!control.value) return null;
    const hasUppercase = /[A-Z]/.test(control.value);
    return hasUppercase ? null : { hasUppercase: true };
  }

  hasLowercaseValidator = (control: any) => {
    if (!control.value) return null;
    const hasLowercase = /[a-z]/.test(control.value);
    return hasLowercase ? null : { hasLowercase: true };
  }

  hasNumberValidator = (control: any) => {
    if (!control.value) return null;
    const hasNumber = /[0-9]/.test(control.value);
    return hasNumber ? null : { hasNumber: true };
  }

  hasSpecialCharValidator = (control: any) => {
    if (!control.value) return null;
    const hasSpecialChar = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(control.value);
    return hasSpecialChar ? null : { hasSpecialChar: true };
  }

  passwordMatchValidator = (form: any) => {
    const newPassword = form.get('newPassword');
    const confirmPassword = form.get('confirmPassword');
    
    if (newPassword && confirmPassword && newPassword.value !== confirmPassword.value) {
      confirmPassword.setErrors({ passwordMismatch: true });
      return { passwordMismatch: true };
    }
    
    if (confirmPassword && confirmPassword.hasError('passwordMismatch') && newPassword.value === confirmPassword.value) {
      confirmPassword.setErrors(null);
    }
    
    return null;
  }

  resetPasswordForm = this.fb.group({
    newPassword: ['', [
      Validators.required,
      Validators.minLength(8),
      this.hasUppercaseValidator,
      this.hasLowercaseValidator,
      this.hasNumberValidator,
      this.hasSpecialCharValidator
    ]],
    confirmPassword: ['', [Validators.required]]
  }, { validators: this.passwordMatchValidator });

  // Helper methods to check validation status
  getPasswordValidationStatus() {
    const password = this.resetPasswordForm.get('newPassword')?.value || '';
    return {
      minLength: password.length >= 8,
      hasUppercase: /[A-Z]/.test(password),
      hasLowercase: /[a-z]/.test(password),
      hasNumber: /[0-9]/.test(password),
      hasSpecialChar: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)
    };
  }

  ngOnInit(): void {
    // Extract token from query parameters
    this.route.queryParams.subscribe(params => {
      const token = params['token'];
      if (token) {
        this.token.set(token);
      } else {
        this.errorService.showError('Invalid or missing reset token');
        this.router.navigate(['/admin/login']);
      }
    });
  }

  get logoUrl(): string {
    return '/assets/images/logo.png';
  }

  get backgroundImageUrl(): string {
    return '/assets/images/login-bg.jpg';
  }

  async onSubmit(): Promise<void> {
    if (this.resetPasswordForm.valid && this.token()) {
      this.isLoading.set(true);
      
      try {
        const { newPassword } = this.resetPasswordForm.value;
        const resetUrl = ApiUrlBuilder.buildFullUrl(AuthUrls.RESET_PASSWORD, this.baseUrl);
        
        const response = await firstValueFrom(
          this.http.post<any>(resetUrl, {
            token: this.token(),
            newPassword: newPassword
          })
        );

        if (response.success) {
          this.passwordReset.set(true);
          this.errorService.showSuccess(response.message || 'Password reset successfully');
          // Redirect to login after 2 seconds
          setTimeout(() => {
            this.router.navigate(['/admin/login']);
          }, 2000);
        } else {
          this.errorService.showError(response.message || 'Failed to reset password');
        }
      } catch (error: any) {
        console.error('Password reset error:', error);
       this.errorService.showError(error?.error?.error?.details?.validationErrors?.join('') || 'Failed to reset password. The token may be invalid or expired.');
      } finally {
        this.isLoading.set(false);
      }
    } else {
      Object.keys(this.resetPasswordForm.controls).forEach(key => {
        this.resetPasswordForm.get(key)?.markAsTouched();
      });
    }
  }

  goToLogin(): void {
    this.router.navigate(['/admin/login']);
  }
}


